import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useFacility } from '../../context/FacilityContext';
import AccountForm from '../../Components/Accounts/AccountForm/AccountForm';
import {
  createOrEditUserFunction,
  deleteUserFunction,
} from '../../firebase/auth';
import { Alert, Box } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase/config';
import { collection, onSnapshot, or, query, where } from 'firebase/firestore';
import AccountTable from '../../Components/Accounts/AccountTable/AccountTable';
import facilities from '../../config/facilities.json';
import AccountFilter from '../../Components/Accounts/AccountFilter/AccountFilter';
import { getPossibleResidentsFunction } from '../../firebase/functions';

function AccountManager() {
  const { facilityId, _ } = useFacility();
  const { claims, currentUser } = useAuth();
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [possibleResidents, setPossibleResidents] = useState([]);

  const [isEditing, setEditing] = useState(false);

  const [filter, setFilter] = useState({ email: '', lastName: '', role: '' });

  const handleLoading = (state) => {
    setIsLoading(state);
  };

  // filter users by email, last name, and role
  useEffect(() => {
    if (!facilityId) return;
    setIsLoading(true);
    let conditions = [where('facilities', 'array-contains', facilityId)];
    if (!claims.superadmin) {
      conditions.push(where('admin', '==', false));
      conditions.push(where('superadmin', '==', false));
    }

    if (filter.email) {
      conditions.push(where('email', '==', filter.email));
    }
    if (filter.lastName) {
      conditions.push(where('lastName', '==', filter.lastName));
    }
    if (filter.role) {
      conditions.push(where(filter.role, '==', true));
    }

    const q = query(collection(db, 'users'), ...conditions);

    setEditing(false);
    formRef.current.resetForm(facilityId);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      getPossibleResidentsFunction({ facilityId: facilityId })
        .then((result) => {
          const possible = result.data?.residents ?? [];
          const usersArray = querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          usersArray.forEach((u) => {
            u.facilities = u.facilities.map((f) =>
              facilities.find((x) => x.id === f),
            );
            u.assignedResidents = u.assignedResidents.map((f) =>
              possible.find((x) => x.id === f),
            );
          });
          setUsers(usersArray);
          setPossibleResidents(possible);
        })
        .catch((error) => {
          setPossibleResidents([]);
          setErrorAlert(error?.message);
        })
        .finally(() => setIsLoading(false));
    });

    return () => unsubscribe();
  }, [claims, facilityId, filter]);

  // useEffect(() => {
  //   if (!facilityId) return;
  //   getPossibleResidentsFunction({ facilityId: facilityId })
  //     .then((result) => {
  //       setPossibleResidents(result.data?.residents ?? []);
  //     })
  //     .catch((error) => {
  //       setPossibleResidents([]);
  //       setErrorAlert(error?.message);
  //     });
  // }, [claims, facilityId]);

  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');
  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const submitCreateUser = (formData) => {
    resetAlerts();
    if (!isEditing && formData.password !== formData.passwordConfirm) {
      setErrorAlert('Passwords do not match. Please try again.');
      return;
    }
    if (!isEditing && formData.password.length < 8) {
      setErrorAlert('Password has to be longer than 8 characters.');
      return;
    }

    const email = formData.email;
    const wasResident = users.find((u) => u.email === email)?.resident ?? false;
    if (isEditing && formData.role !== 'resident' && wasResident) {
      setErrorAlert("Resident's role cannot be changed.");
      return;
    }
    setIsLoading(true);
    createOrEditUserFunction({
      ...formData,
      isEdit: isEditing,
      editorId: currentUser.uid,
    })
      .then((result) => {
        if (formRef.current) {
          formRef.current.resetForm(facilityId);
        }
        if (result.data?.isNewUser) {
          setInfoAlert(
            `Successfully created new account '${result.data.email}'!`,
          );
        } else {
          setInfoAlert(`Successfully edited account '${result.data.email}'!`);
        }

        setIsLoading(false);
        setEditing(false);
      })
      .catch((error) => {
        console.error('Error creating user:', error);
        setErrorAlert(error?.message);
        setIsLoading(false);
      });
  };

  const editUser = (user) => {
    setEditing(true);
    resetAlerts();
    if (formRef.current) {
      let role = 'user';
      if (user.superadmin) {
        role = 'superadmin';
      } else if (user.admin) {
        role = 'admin';
      } else if (user.resident) {
        role = 'resident';
      } else if (user.staff) {
        role = 'staff';
      }
      const data = {
        email: user.email,
        phone: user?.phone,
        password: '',
        role: role,
        staffPermissions: user.staffPermissions ?? [],
        adminPermissions: user.adminPermissions ?? [],
        facilities: user.facilities.map((f) => f.id),
        assignedResidents: user.assignedResidents.map((f) => f.id),
        firstName: user.firstName,
        lastName: user.lastName,
      };
      formRef.current.setForm(data);
    }
  };

  const deleteUser = (user) => {
    resetAlerts();
    if (
      !window.confirm(
        `Are you sure you want to remove user ${user.email}? This action is irreversible!`,
      )
    ) {
      return;
    }
    setIsLoading(true);
    const data = { email: user.email, editorId: currentUser.uid };
    deleteUserFunction(data)
      .then((result) => {
        if (result.data?.deleted) {
          setInfoAlert(`Successfully deleted account '${result.data.email}'!`);
        }

        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error deleting user:', error);
        setErrorAlert(error?.message);
        setIsLoading(false);
      });
  };

  const cancelEditing = () => {
    resetAlerts();
    setEditing(false);
    formRef.current.resetForm(facilityId);
  };

  return (
    <div>
      <DashboardLayout
        facilityId={facilityId}
        isLoading={isLoading}
        setIsLoading={handleLoading}
      >
        <Box sx={{ maxWidth: 1280 }}>
          {!isEditing && (
            <h2 style={{ marginBottom: '10px' }}>Create New Account</h2>
          )}
          {isEditing && <h2 style={{ marginBottom: '10px' }}>Edit Account</h2>}

          {infoAlert && (
            <Alert
              severity="success"
              onClose={() => {
                setInfoAlert('');
              }}
            >
              {infoAlert}
            </Alert>
          )}
          {errorAlert && (
            <Alert
              severity="error"
              onClose={() => {
                setErrorAlert('');
              }}
            >
              {errorAlert}
            </Alert>
          )}

          <AccountForm
            ref={formRef}
            onSubmit={submitCreateUser}
            allowAll={claims?.superadmin ?? false}
            allowedFacilities={claims?.facilities ?? []}
            userOnly={!claims?.superadmin}
            editMode={isEditing}
            onCancel={cancelEditing}
            facilityId={claims?.superadmin ? '' : facilityId}
            assignedResidents={possibleResidents}
          />
        </Box>
        <hr />
        <Box sx={{ marginTop: '15px' }}>
          <AccountFilter onFilter={setFilter} userOnly={!claims?.superadmin} />
          <AccountTable
            users={users}
            onEdit={editUser}
            onDelete={deleteUser}
            canDeleteUsersOnly={!claims?.superadmin}
          />
        </Box>
      </DashboardLayout>
    </div>
  );
}

AccountManager.propTypes = {};

export default AccountManager;
